/**
 * Wave 3.46 — Customer Master form dialog.
 *
 * Used in two surfaces:
 *   1. The Customers Master page (Add / Edit existing).
 *   2. The New Order Form dialog (inline "+ Add new customer" trigger
 *      surfaced via the CustomerCombobox).
 *
 * Keep the API of this component small + opinionated so both surfaces
 * can swap it in without conditional spaghetti.
 */

import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';

const EMPTY_FORM = {
  name: '',
  phone: '',
  email: '',
  address: '',
  notes: '',
};

export function CustomerFormDialog({
  open,
  onOpenChange,
  companyId,
  customer = null,           // when provided → edit mode
  defaultName = '',          // pre-fill name (used by inline-add from combobox)
  onCreated,                 // (customer) => void  fired after a successful create
  onUpdated,                 // (customer) => void  fired after a successful update
}) {
  const isEdit = !!customer;
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit && customer) {
      setForm({
        name: customer.name || '',
        phone: customer.phone || '',
        email: customer.email || '',
        address: customer.address || '',
        notes: customer.notes || '',
      });
    } else {
      setForm({ ...EMPTY_FORM, name: defaultName || '' });
    }
  }, [open, isEdit, customer, defaultName]);

  const submit = async () => {
    const name = (form.name || '').trim();
    if (!name) {
      toast.error('Customer name is required');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.put(`/masters/customers/${customer.id}`, {
          name,
          phone: (form.phone || '').trim(),
          email: (form.email || '').trim(),
          address: (form.address || '').trim(),
          notes: (form.notes || '').trim(),
        });
        toast.success(`Updated "${data?.name || name}"`);
        onOpenChange(false);
        if (onUpdated) await onUpdated(data);
      } else {
        const { data } = await api.post('/masters/customers', {
          name,
          phone: (form.phone || '').trim(),
          email: (form.email || '').trim(),
          address: (form.address || '').trim(),
          notes: (form.notes || '').trim(),
          company_id: companyId,
        });
        toast.success(`Added "${data?.name || name}"`);
        onOpenChange(false);
        if (onCreated) await onCreated(data);
      }
    } catch (e) {
      toast.error(extractError(e, isEdit ? 'Failed to update customer' : 'Failed to add customer'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="customer-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the customer record. Renaming will also refresh the name on any non-completed orders that reference this customer.'
              : 'Add a new customer to this company’s master list. Customers can then be attributed to any new order form.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>
              Customer Name <span className="text-rose-600">*</span>
            </Label>
            <Input
              autoFocus
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Mrs. Sharma"
              data-testid="customer-form-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                placeholder="+91 90000 00000"
                data-testid="customer-form-phone-input"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="name@example.com"
                data-testid="customer-form-email-input"
              />
            </div>
          </div>
          <div>
            <Label>Address</Label>
            <Textarea
              value={form.address}
              onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
              rows={2}
              placeholder="Optional — shipping / billing address"
              data-testid="customer-form-address-input"
            />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              rows={2}
              placeholder="Internal notes (preferences, referrals, anniversaries, etc.)"
              data-testid="customer-form-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !(form.name || '').trim()}
            className="luxury-button"
            data-testid="customer-form-submit-button"
          >
            {saving ? (isEdit ? 'Saving…' : 'Adding…') : isEdit ? 'Save Changes' : 'Add Customer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CustomerFormDialog;
